import { Platform } from "react-native";

import { ROLE_COLORS } from "../theme/boardColors";

/**
 * Web-specific optimizations and utilities
 */

/**
 * Add web-specific meta tags for better SEO and performance
 */
export const addWebMetaTags = (): void => {
  if (Platform.OS !== "web" || typeof document === "undefined") {
    return;
  }

  // Add favicon links first
  const faviconLinks = [
    { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
    { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon.png" },
    { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
    { rel: "apple-touch-icon", sizes: "180x180", href: "/favicon.png" },
  ];

  faviconLinks.forEach(({ rel, type, sizes, href }) => {
    const existingLink = document.querySelector(`link[rel="${rel}"]`);
    if (!existingLink) {
      const link = document.createElement("link");
      link.rel = rel;
      if (type) link.type = type;
      if (sizes) link.setAttribute("sizes", sizes);
      link.href = href;
      document.head.appendChild(link);
    }
  });

  const metaTags = [
    {
      name: "description",
      content: "Find the best route through word meanings",
    },
    {
      name: "keywords",
      content:
        "word game, puzzle, semantic relationships, AI challenge, daily puzzle, word navigation",
    },
    { name: "author", content: "Synapse Game" },
    {
      name: "viewport",
      content:
        "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no",
    },
    { name: "theme-color", content: "#121212" },
    // Open Graph tags (Facebook, Discord, LinkedIn, etc.)
    { property: "og:title", content: "Synapse: Semantic Pathways" },
    {
      property: "og:description",
      content: "Find the best route through word meanings",
    },
    { property: "og:type", content: "website" },
    { property: "og:image", content: "/preview.png" },
    { property: "og:image:width", content: "2400" },
    { property: "og:image:height", content: "1260" },
    { property: "og:image:alt", content: "Synapse word connection challenge" },
    { property: "og:url", content: "https://synapse.gbe.games" },
    { property: "og:site_name", content: "Synapse Game" },
    // Twitter Card tags
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "Synapse: Semantic Pathways" },
    {
      name: "twitter:description",
      content: "Find the best route through word meanings",
    },
    { name: "twitter:image", content: "/preview.png" },
    { name: "twitter:image:alt", content: "Synapse word connection challenge" },
  ];

  metaTags.forEach(({ name, property, content }) => {
    const existingTag = document.querySelector(
      `meta[${name ? "name" : "property"}="${name || property}"]`,
    );
    if (!existingTag) {
      const meta = document.createElement("meta");
      if (name) meta.name = name;
      if (property) meta.setAttribute("property", property);
      meta.content = content;
      document.head.appendChild(meta);
    }
  });
};

/**
 * Optimize images for web using modern formats when available
 */
export const optimizeImageLoading = (): void => {
  if (Platform.OS !== "web" || typeof window === "undefined") {
    return;
  }

  // Add support for WebP and AVIF images
  const images = document.querySelectorAll("img[data-src]");

  if ("IntersectionObserver" in window) {
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          const src = img.dataset.src;
          if (src) {
            img.src = src;
            img.removeAttribute("data-src");
            imageObserver.unobserve(img);
          }
        }
      });
    });

    images.forEach((img) => imageObserver.observe(img));
  }
};

/**
 * Visible keyboard focus: a 2px gold ring on any focus-visible element. The
 * browser's default 1px outline disappears against the blue tutorial hint.
 */
export const injectFocusStyles = (): void => {
  if (Platform.OS !== "web" || typeof document === "undefined") {
    return;
  }
  if (document.getElementById("synapse-focus-style")) return;
  const style = document.createElement("style");
  style.id = "synapse-focus-style";
  style.textContent = `:focus-visible { outline: 2px solid ${ROLE_COLORS.gold} !important; outline-offset: 2px; }`;
  document.head.appendChild(style);
};

/**
 * Initialize all web optimizations
 */
export const initializeWebOptimizations = (): void => {
  if (Platform.OS !== "web") {
    return;
  }

  // Run optimizations
  addWebMetaTags();
  injectFocusStyles();
  optimizeImageLoading();

  // Add performance observer for monitoring
  if ("PerformanceObserver" in window) {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        // Log slow operations in development
        if (__DEV__ && entry.duration > 100) {
          console.warn(
            `Slow operation detected: ${entry.name} took ${entry.duration}ms`,
          );
        }
      });
    });

    observer.observe({ entryTypes: ["measure", "navigation"] });
  }
};
